/**
 * Turkish identity and tax numbers, and the name rules travel documents follow.
 * Shared by the API (which enforces) and the console (which warns as you type).
 */

const digits = (value: string) => [...value].map(Number);

/**
 * T.C. kimlik numarası: 11 digits, no leading zero, two check digits.
 * d10 = ((d1+d3+d5+d7+d9)·7 − (d2+d4+d6+d8)) mod 10, d11 = (d1+…+d10) mod 10.
 */
export function isValidTckn(value: string): boolean {
  if (!/^[1-9]\d{10}$/.test(value)) return false;
  const d = digits(value);
  const odd = d[0]! + d[2]! + d[4]! + d[6]! + d[8]!;
  const even = d[1]! + d[3]! + d[5]! + d[7]!;
  const tenth = (((odd * 7 - even) % 10) + 10) % 10;
  const eleventh = d.slice(0, 10).reduce((a, b) => a + b, 0) % 10;
  return d[9] === tenth && d[10] === eleventh;
}

/** Vergi kimlik numarası: 10 digits with the Revenue Administration's check digit. */
export function isValidVkn(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const d = digits(value);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const t = (d[i]! + (9 - i)) % 10;
    let v = (t * 2 ** (9 - i)) % 9;
    if (t !== 0 && v === 0) v = 9;
    sum += v;
  }
  return (10 - (sum % 10)) % 10 === d[9];
}

/** A company's tax number is a VKN, or a TCKN for a sole proprietorship. */
export function isValidTaxNumber(value: string): boolean {
  return value.length === 11 ? isValidTckn(value) : isValidVkn(value);
}

/**
 * An IBAN, checked the ISO 13616 way: country, check digits, then the account;
 * moving the first four characters to the end and reading letters as numbers
 * (A = 10 … Z = 35) must leave remainder 1 modulo 97. A Turkish IBAN has 26
 * characters. Spaces are ignored.
 */
export function isValidIban(value: string): boolean {
  const iban = value.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  if (iban.startsWith('TR') && iban.length !== 26) return false;
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (c) =>
    String(c.charCodeAt(0) - 55),
  );
  let rest = 0;
  for (const d of digits) rest = (rest * 10 + Number(d)) % 97;
  return rest === 1;
}

/** "TR330006100519786457841326" as "TR33 0006 1005 1978 6457 8413 26". */
export function formatIban(value: string): string {
  return value
    .replace(/\s/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

/**
 * Names are stored the way they go on a ticket: Turkish upper case, single spaces.
 * "şen  tahir" → "ŞEN TAHİR" (note İ, not I).
 */
export function normalizePersonName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleUpperCase('tr-TR');
}

/**
 * Shows only the last two characters of an identity number to someone without
 * `customers.identity`. Enough to tell two passports apart, not enough to use one.
 */
export function maskIdentifier(value: string): string {
  if (value.length <= 2) return '••';
  return '•'.repeat(Math.min(value.length - 2, 9)) + value.slice(-2);
}

/** Days from today until `isoDate` (negative when past). */
export function daysUntil(isoDate: string, today = new Date()): number {
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const [y, m, d] = isoDate.split('-').map(Number);
  return Math.round((Date.UTC(y!, m! - 1, d!) - start) / 86_400_000);
}

/**
 * Most countries require a passport valid six months beyond the trip. The console
 * flags documents inside that window.
 */
export type ExpiryState = 'valid' | 'expiring' | 'expired';

export function expiryState(isoDate: string | null, today = new Date()): ExpiryState | null {
  if (!isoDate) return null;
  const days = daysUntil(isoDate, today);
  if (days < 0) return 'expired';
  if (days < 183) return 'expiring';
  return 'valid';
}
